import { message } from 'antd'
import {
  useRolodexEntries,
  useRolodexFileLSBulk,
  useRolodexSignedURLs,
} from 'features/rolodex'
import { useCallback, useMemo, useState } from 'react'
import { GeckoReport, RolodexEntryType } from 'types'
import { downloadBlob } from 'utils/download'

interface UseInspectionReportDownloadParams {
  unitId?: string
  inspectionId?: string
}

interface UseInspectionReportDownloadResult {
  reportCount: number
  hasSignedUrls: boolean
  isLoading: boolean
  isDownloading: boolean
  handleDownloadReport: () => Promise<void>
}

export const useInspectionReportDownload = ({
  unitId,
  inspectionId,
}: UseInspectionReportDownloadParams): UseInspectionReportDownloadResult => {
  const [isDownloading, setIsDownloading] = useState(false)

  const reportQuery = useMemo(() => {
    if (!unitId || !inspectionId) return undefined
    return {
      type: RolodexEntryType.REPORT,
      tags: { unit: unitId },
      filters: [{ key: 'file_category', op: '=', value: 'gecko_report' }],
      limit: 100,
    }
  }, [unitId, inspectionId])

  const { reports } = useRolodexEntries(reportQuery)

  const matchingReports: ReadonlyArray<GeckoReport> = useMemo(() => {
    if (!inspectionId) return []
    return reports.filter((report) =>
      (report.data.inspections ?? []).includes(inspectionId)
    )
  }, [reports, inspectionId])

  const fileListQueries = useMemo(() => {
    if (matchingReports.length === 0) return undefined
    return matchingReports.map((report) => ({
      entry_id: report.id,
      path: '**',
    }))
  }, [matchingReports])

  const { fileRefs, isLoading: isLoadingFiles } =
    useRolodexFileLSBulk(fileListQueries)

  const { results: signedUrlResults, isLoading: isLoadingUrls } =
    useRolodexSignedURLs(fileRefs.length > 0 ? fileRefs : undefined)

  const handleDownloadReport = useCallback(async () => {
    if (signedUrlResults.length === 0) {
      message.warning('No report files available for download')
      return
    }

    setIsDownloading(true)
    try {
      for (const result of signedUrlResults) {
        const report = matchingReports.find((r) => r.id === result.entry_id)
        const filename =
          report?.data.visibleName ||
          result.path ||
          `report-${result.entry_id}.pdf`

        const response = await fetch(result.signed_url)
        const blob = await response.blob()
        downloadBlob(blob, filename)
      }
    } catch (error) {
      console.error('Error downloading report:', error)
      message.error('Failed to download report')
    } finally {
      setIsDownloading(false)
    }
  }, [signedUrlResults, matchingReports])

  const isLoading = isLoadingFiles || isLoadingUrls

  return {
    reportCount: matchingReports.length,
    hasSignedUrls: signedUrlResults.length > 0,
    isLoading,
    isDownloading,
    handleDownloadReport,
  }
}
